import React, { useMemo, useState } from 'react';
import Modal from 'src/components/common/ui/Modal';

export default function BackfillHistoryModal({
  open,
  onClose,
  onRun,
}: {
  open: boolean;
  onClose: () => void;
  onRun: (x: { targetEvToPvRatio: number; pattern: 'UNIFORM' | 'FRONT_DELAY' | 'MID_DIP' }) => Promise<void>;
}) {
  const [ratio, setRatio] = useState(0.95);
  const [pattern, setPattern] = useState<'UNIFORM' | 'FRONT_DELAY' | 'MID_DIP'>('UNIFORM');
  const [busy, setBusy] = useState(false);

  const canRun = useMemo(() => ratio > 0 && ratio <= 1, [ratio]);

  const handleRun = async () => {
    if (!canRun || busy) return;
    setBusy(true);
    try {
      await onRun({ targetEvToPvRatio: ratio, pattern });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal size='xl' open={open} onClose={onClose} title="Backfill Progress History">
      <div className="space-y-5">

        {/* 안내 박스 */}
        <div className="bg-blue-50 border border-blue-100 text-blue-800 text-sm rounded p-3 leading-relaxed">
          과거 주차의 실적(EV)을 자동 생성합니다.  
          이미 사용자가 입력한 실적은 변경되지 않습니다.
        </div>

        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded p-3 leading-relaxed">
          기본 기준 요일은 <b>금요일</b>입니다. 다른 기준 요일이 필요하면 <b>Backfill 실행 전에</b> 프로젝트 설정에서 먼저
          변경하세요.
          <br />
          EV 제출 데이터가 1건이라도 생성되면 기준 요일은 더 이상 변경할 수 없습니다.
        </div>

        {/* EV 비율 */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">EV / PV 최소 비율</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={ratio}
              onChange={(e) => setRatio(Number(e.target.value))}
              className="w-24 px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <span className="text-sm text-gray-500">
              → 실제 EV는 PV의 <b>{Math.round(ratio * 100)}% ~ 100%</b> 범위에서 랜덤 생성
            </span>
          </div>
        </div>

        {/* Pattern */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">EV 분포 패턴</label>
          <select
            className="w-full px-2 py-1.5 border rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={pattern}
            onChange={(e) => setPattern(e.target.value as any)}
          >
            <option value="UNIFORM">UNIFORM</option>
            <option value="FRONT_DELAY">FRONT_DELAY</option>
            <option value="MID_DIP">MID_DIP</option>
          </select>

          <div className="text-xs text-gray-500 leading-snug pt-1">
            <div><b>UNIFORM</b> — EV가 PV를 일정 비율로 고르게 따라갑니다.</div>
            <div><b>FRONT_DELAY</b> — 초반 실적이 낮고 후반으로 갈수록 만회되는 형태입니다.</div>
            <div><b>MID_DIP</b> — 중간에 실적이 일시적으로 떨어졌다가 회복되는 형태입니다.</div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div className="pt-2 flex justify-end gap-2 border-t">
          <button
            type="button"
            className="px-4 py-1.5 text-sm rounded border bg-white hover:bg-gray-50"
            onClick={onClose}
            disabled={busy}
          >
            취소
          </button>
          <button
            type="button"
            className="px-4 py-1.5 text-sm rounded text-white bg-gray-900 hover:bg-gray-700 disabled:opacity-50"
            onClick={handleRun}
            disabled={!canRun || busy}
          >
            {busy ? '생성 중…' : 'Backfill 실행'}
          </button>
        </div>

      </div>
    </Modal>
  );
}
